import * as React from 'react';
import ReactHotToast from 'react-hot-toast';

import { trackEvent } from '@/analytics/trackEvent';
import { IconButton } from '@/components/IconButton/IconButton';
import { NEEDL_UNIVERSE_ID } from '@/features/ChatBotV2/context';
import { useHandleFilesDrop } from '@/features/ChatBotV2/hooks/useHandleFilesDrop';
import { withAskNeedlStateSlice } from '@/features/ChatBotV2/hooks/withStateSlice';
import type { AttachmentType, Message } from '@/features/ChatBotV2/types';
import { useFileHandler } from '@/features/ReportGeneration/hooks';
import { useConfigUI } from '@/hooks/query/useFeatureConfig';
import { useFetchUploadConfig } from '@/hooks/useFetchUploadConfig';
import { getMimeTypeFromFileType } from '@/utils/fileAndMimeTypes';
import { getLogo } from '@/utils/getIcons';

export const AttachFilesInputImpl = ({
  currentFeedOrDocumentId,
  messages,
  attachmentsFeedId,
  attachments,
  isUploadCompleted,
}: AttachFilesInputImplType) => {
  const AttachmentIcon = getLogo('attachment');
  const { handleFilesDrop } = useHandleFilesDrop(
    attachmentsFeedId,
    attachments
  );
  const { isLoading: isFeatureConfigLoading, data: featureConfig } =
    useConfigUI();

  const { isLoading: isUploadConfigLoading, data: uploadConfig } =
    useFetchUploadConfig({
      source: 'ask_needl',
    });

  const mimeTypes = React.useMemo(
    () =>
      uploadConfig?.file_types.map((fileType) =>
        getMimeTypeFromFileType(fileType)
      ) || [],
    [uploadConfig?.file_types]
  );

  const { fileInputRef, handleFileSelect, handleFileInputClick } =
    useFileHandler(
      handleFilesDrop,
      {
        file_types: uploadConfig?.file_types || [],
        file_count_limit: uploadConfig?.file_count_limit || 0,
        file_size_limit: uploadConfig?.file_size_limit || 0,
        mime_types: mimeTypes as string[],
        allow_folder_upload: false,
        file_name_suffixes: [],
      },
      {
        ...(featureConfig?.needl_report_open_research
          ? {
              onSizeLimitExceed: () => {
                ReactHotToast.error(getErrorMessage('file_size_limit'), {
                  duration: 3000,
                  style: {
                    maxWidth: '100%',
                  },
                });
              },
            }
          : {}),
      }
    );

  const isUploadDisabled =
    isUploadConfigLoading ||
    isFeatureConfigLoading ||
    currentFeedOrDocumentId === NEEDL_UNIVERSE_ID ||
    (!attachmentsFeedId && messages.length > 0) ||
    !isUploadCompleted;

  return (
    <>
      <IconButton
        type='button'
        size='small'
        onClick={() => {
          trackEvent('askneedl_upload_clicked');
          handleFileInputClick('file');
        }}
        disabled={isUploadDisabled}
      >
        <AttachmentIcon className='text-gray-700' />
      </IconButton>
      <input
        ref={fileInputRef}
        className='hidden'
        type='file'
        accept={mimeTypes.map((type) => `.${type}`).join(',')}
        multiple
        onChange={handleFileSelect}
      />
    </>
  );
};

export const AttachFilesInput = withAskNeedlStateSlice<
  Record<string, unknown>,
  AttachFilesInputImplType
>(AttachFilesInputImpl, (props, state) => {
  const searchWithin = state.data.searchWithin;
  const currentFeedOrDocumentId = state.data.currentFeedOrDocumentId;
  const attachments =
    state.data.sessions[searchWithin][currentFeedOrDocumentId]?.attachments ||
    [];
  const isUploadCompleted = attachments.every(
    (attachment) => attachment.status === 'COMPLETED'
  );

  return {
    ...props,
    currentFeedOrDocumentId,
    messages:
      state.data.sessions[searchWithin][currentFeedOrDocumentId].messages,
    attachmentsFeedId:
      state.data.sessions[searchWithin][currentFeedOrDocumentId]
        .attachmentsFeedId || '',
    attachments,
    isUploadCompleted,
  };
});

export const getErrorMessage = (type: GET_ERROR_MESSAGE_TYPE) => {
  switch (type) {
    case 'file_size_limit':
      return 'Too large to upload here. Try NeedlReport Open Research for files over 50MB.';
    default:
      return 'Unknown error';
  }
};

type GET_ERROR_MESSAGE_TYPE = 'file_size_limit';

type AttachFilesInputImplType = {
  currentFeedOrDocumentId: string;
  messages: Message[];
  attachmentsFeedId: string;
  attachments: AttachmentType[];
  isUploadCompleted: boolean;
};
